import { Injectable } from '@angular/core';

import { IPbisType } from 'libs/domain';

import { CacheService } from '@app/src/app/shared/services/cache/cache.service';
import { CacheKey } from '@app/src/app/shared/services/cache/cache.types';

import { ModalOverlayService, ModalOverlayServiceCloseEventType } from '@shared/components/modal-overlay';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';

import { BehaviorManagerService } from '../../../services';
import { BmTypesBehaviorEditComponent } from '../components/bm-types-behavior-edit/bm-types-behavior-edit.component';
import { BmTypesBehaviorEditModalData, BmTypesBehaviorEditModalResponse } from '../types';

@Injectable()
export class BmTypesBehaviorService {
  /** Service Constructor */
  constructor(
    private _systemSnackbar: SystemAlertSnackBarService,
    private _modalOverlay: ModalOverlayService<BmTypesBehaviorEditModalResponse, BmTypesBehaviorEditModalData>,
    private _bmService: BehaviorManagerService,
    private _cacheService: CacheService,
  ) {}

  public async openEditModal(id?: number): Promise<void> {
    const editModal = this._modalOverlay.open(BmTypesBehaviorEditComponent, {
      data: { id },
      disposeOnNavigation: false,
    });
    editModal.afterClosed.subscribe(async response => {
      if (!response) return;
      const { type } = response;
      switch (type) {
        case ModalOverlayServiceCloseEventType.CREATE: {
          this._systemSnackbar.success('Successfully created new behavior type');
          this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
          return;
        }
        case ModalOverlayServiceCloseEventType.SUBMIT: {
          await this._bmService.fetchTypes();
          this._systemSnackbar.success('Successfully updated behavior type');
          this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
          return;
        }
        case ModalOverlayServiceCloseEventType.DELETE: {
          this._systemSnackbar.success('Successfully deleted behavior type');
          this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
          return;
        }
        default: {
          return;
        }
      }
    });
  }

  public async changeStatus(behaviorType: IPbisType, status: boolean): Promise<void> {
    try {
      const type = { ...behaviorType };
      type.active = status;

      // restrictions are unaffected so there is no need to pass them along and upsert them
      type.restriction = undefined;

      await this._bmService.updateType(type);
      this._systemSnackbar.success(`Successfully set ${type.name} to ${status ? 'active' : 'inactive'}`);
      this._cacheService.clearCacheByNamespace(CacheKey.SELECTION_ASSIGNER_CAROUSEL);
    } catch (error) {
      this._systemSnackbar.error(`Failed to update behavior status`);
    }
  }
}
